import type { Midi, MidiProgramChange } from "@midi-fantom-utils/midi-core";
import { DRUM_CHANNEL, getInstrumentMeta } from "@midi-fantom-utils/midi-core";

/**
 * Применяет переопределения инструментов каналов (GM program 0..127) к
 * per-channel `Midi` (вход — результат `toMultiTrackFormat`). Для каждого
 * override `(channel, program)` находит трек с `track.channel === channel`,
 * заменяет его `programChanges` на `[{ ticks: 0, value: program, channel }]`
 * и обновляет `track.instrument` через `getInstrumentMeta` (name/family/
 * number/percussion). Ноты/CC/pitch bend не меняются.
 *
 * Чистая функция: возвращает исходный `midi` (ref-равенство) при пустом
 * `overrides` — это дешёво и безопасно для useMemo. Канал 9 (ударные) не
 * редактируется (instrument ударных определяет сам soundfont).
 */
export const applyChannelPrograms = (
  midi: Midi,
  overrides: ReadonlyMap<number, number>
): Midi => {
  if (overrides.size === 0) return midi;
  let changed = false;
  const tracks = midi.tracks.map((track) => {
    const program = overrides.get(track.channel);
    if (program === undefined) return track;
    const channel = track.channel;
    const programChange: MidiProgramChange = { ticks: 0, value: program, channel };
    const meta = getInstrumentMeta(program, channel);
    changed = true;
    return {
      ...track,
      programChanges: [programChange],
      instrument: {
        number: program,
        family: meta.family,
        name: meta.name,
        percussion: meta.percussion,
      },
    };
  });
  return changed
    ? { ...midi, tracks }
    : midi;
};

/**
 * GM-программа канала в `effectiveMidi` (для селектора инструмента в чипе).
 * Приоритет: первый `programChanges[0].value`, иначе `instrument.number`.
 * Канал 9 всегда возвращает `0` (GM drum map применяется автоматически).
 */
export const channelProgram = (midi: Midi, channel: number): number => {
  if (channel === DRUM_CHANNEL) return 0;
  const track = midi.tracks.find((t) => t.channel === channel);
  if (!track) return 0;
  const pc = track.programChanges[0];
  if (pc && Number.isFinite(pc.value)) return Math.max(0, Math.min(127, Math.round(pc.value)));
  const instrumentNumber = track.instrument.number;
  if (Number.isFinite(instrumentNumber)) {
    return Math.max(0, Math.min(127, Math.round(instrumentNumber)));
  }
  return 0;
};
